import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
} from '@mui/material';
import {
  Event,
  Add,
  Warning,
  CheckCircle,
  Schedule,
  TrendingUp,
  SmartToy,
  Notifications,
  Assignment,
  AccountBalance,
  Assessment,
  Security
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { format, addDays, isBefore, isAfter } from 'date-fns';
import { useSnackbar } from 'notistack';
import api from '../services/api';
import { regulatoryIntelligenceService } from '../services/regulatoryIntelligenceService';
import { LoggerService } from '../services/LoggerService';

// Types
interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  event_date: string;
  event_type: string;
  importance: string;
  regulatory_body: string;
  jurisdiction: string;
  affected_entities: string[];
  requirements: string[];
  ai_generated: boolean;
  ai_confidence?: number;
  status: string;
  created_at: string;
  recommendations?: string[];
}

interface CalendarFilters {
  start_date?: Date;
  end_date?: Date;
  event_types?: string[];
  jurisdictions?: string[];
  importance_levels?: string[];
  ai_generated_only?: boolean;
}

interface CalendarStatistics {
  summary: {
    total_events: number;
    critical_events: number;
    high_priority_events: number;
    ai_generated_events: number;
    upcoming_deadlines: number;
  };
  regulatory_bodies: Array<{ body: string; count: number }>;
  event_types: Array<{ type: string; count: number }>;
  period: { start_date: string; end_date: string };
}

const RegulatoryCalendar: React.FC = () => {
  // State
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [statistics, setStatistics] = useState<CalendarStatistics | null>(null);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState<CalendarEvent[]>([]);
  const [filters, _setFilters] = useState<CalendarFilters>({
    start_date: new Date(),
    end_date: addDays(new Date(), 90)
  });
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    event_date: new Date(),
    event_type: 'deadline',
    importance: 'medium',
    regulatory_body: '',
    jurisdiction: 'EU',
    affected_entities: [] as string[],
    requirements: [] as string[]
  });
  const [aiRequest, setAIRequest] = useState({
    jurisdiction: 'EU',
    time_horizon_days: 90,
    model_name: 'gpt-4'
  });

  // Constants
  const eventTypes = ['deadline', 'review', 'audit', 'submission', 'inspection', 'training', 'renewal'];
  const importanceLevels = ['low', 'medium', 'high', 'critical'];
  const jurisdictions = ['EU', 'Luxembourg', 'Germany', 'France', 'Belgium'];
  const statusColors = {
    'upcoming': 'primary',
    'in_progress': 'warning',
    'completed': 'success',
    'overdue': 'error',
    'cancelled': 'default'
  } as const;

  const importanceColors = {
    'low': 'default',
    'medium': 'primary',
    'high': 'warning',
    'critical': 'error'
  } as const;

  const eventTypeIcons = {
    'deadline': <Assignment />,
    'review': <Assessment />,
    'audit': <Security />,
    'submission': <CheckCircle />,
    'inspection': <AccountBalance />,
    'training': <Event />,
    'renewal': <Schedule />
  };

  const { enqueueSnackbar } = useSnackbar();

  // Mock data (kept for reference, but not used)
  const mockEvents: CalendarEvent[] = [
    {
      id: '1',
      title: 'CSSF Annual Report Submission',
      description: 'Submit annual regulatory report to Commission de Surveillance du Secteur Financier',
      event_date: format(addDays(new Date(), 15), 'yyyy-MM-dd'),
      event_type: 'deadline',
      importance: 'critical',
      regulatory_body: 'CSSF',
      jurisdiction: 'Luxembourg',
      affected_entities: ['fund_001', 'fund_002'],
      requirements: [
        'Audited financial statements',
        'Risk management report',
        'Compliance certification'
      ],
      ai_generated: false,
      status: 'upcoming',
      created_at: new Date().toISOString(),
      recommendations: [
        'Start preparation 30 days before deadline',
        'Schedule internal review meeting',
        'Prepare supporting documentation'
      ]
    },
    {
      id: '2',
      title: 'ESMA Stress Test Participation',
      description: 'Participation in European Securities and Markets Authority stress testing exercise',
      event_date: format(addDays(new Date(), 25), 'yyyy-MM-dd'),
      event_type: 'submission',
      importance: 'high',
      regulatory_body: 'ESMA',
      jurisdiction: 'EU',
      affected_entities: ['fund_001'],
      requirements: [
        'Portfolio data submission',
        'Risk scenario analysis',
        'Stress test results'
      ],
      ai_generated: true,
      ai_confidence: 78.5,
      status: 'upcoming',
      created_at: new Date().toISOString(),
      recommendations: [
        'Review previous stress test methodologies',
        'Update risk models and parameters',
        'Coordinate with risk management team'
      ]
    }
  ];

  const mockStatistics: CalendarStatistics = {
    summary: {
      total_events: 12,
      critical_events: 2,
      high_priority_events: 5,
      ai_generated_events: 7,
      upcoming_deadlines: 4
    },
    regulatory_bodies: [
      { body: 'CSSF', count: 4 },
      { body: 'ESMA', count: 5 },
      { body: 'BaFin', count: 3 }
    ],
    event_types: [
      { type: 'deadline', count: 6 },
      { type: 'submission', count: 4 },
      { type: 'review', count: 2 }
    ],
    period: {
      start_date: format(new Date(), 'yyyy-MM-dd'),
      end_date: format(addDays(new Date(), 90), 'yyyy-MM-dd')
    }
  };

  // Effects
  useEffect(() => {
    loadEvents();
    loadStatistics();
    loadUpcomingDeadlines();
  }, [filters]);

  // Functions
  const loadEvents = async () => {
    setLoading(true);
    try {
      const startDate = filters.startDate || format(new Date(), 'yyyy-MM-dd');
      const endDate = filters.endDate || format(addDays(new Date(), 90), 'yyyy-MM-dd');
      
      const events = await regulatoryIntelligenceService.getRegulatoryCalendar(startDate, endDate, {
        jurisdiction: filters.jurisdiction ? [filters.jurisdiction] : undefined,
        priority: filters.importance ? [filters.importance] : undefined,
        eventTypes: filters.eventType ? [filters.eventType] : undefined
      });
      
      setEvents(events);
    } catch (error) {
      LoggerService.error('Error loading events:', error);
      enqueueSnackbar('Failed to load calendar events', { variant: 'error' });
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await api.get('/regulatory-calendar/statistics', {
        params: {
          start_date: filters.startDate || format(new Date(), 'yyyy-MM-dd'),
          end_date: filters.endDate || format(addDays(new Date(), 90), 'yyyy-MM-dd')
        }
      });
      setStatistics(response.data);
    } catch (error) {
      LoggerService.error('Error loading statistics:', error);
      enqueueSnackbar('Failed to load statistics', { variant: 'error' });
    }
  };

  const loadUpcomingDeadlines = async () => {
    try {
      const startDate = format(new Date(), 'yyyy-MM-dd');
      const endDate = format(addDays(new Date(), 30), 'yyyy-MM-dd');
      
      const deadlines = await regulatoryIntelligenceService.getRegulatoryCalendar(startDate, endDate, {
        eventTypes: ['deadline']
      });
      
      setUpcomingDeadlines(deadlines.filter(event => 
        isBefore(new Date(), new Date(event.event_date))
      ));
    } catch (error) {
      LoggerService.error('Error loading upcoming deadlines:', error);
    }
  };

  const generateAIEvents = async () => {
    setLoading(true);
    try {
      // Simulate AI generation
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Add mock AI-generated events
      const aiEvents: CalendarEvent[] = [
        {
          id: `ai-${Date.now()}`,
          title: 'SFDR Sustainability Disclosure Update',
          description: 'Update sustainability-related financial disclosures under SFDR',
          event_date: format(addDays(new Date(), 60), 'yyyy-MM-dd'),
          event_type: 'deadline',
          importance: 'high',
          regulatory_body: 'ESMA',
          jurisdiction: aiRequest.jurisdiction,
          affected_entities: ['fund_001', 'fund_002', 'fund_003'],
          requirements: [
            'Principal adverse impact disclosures',
            'Article 8/9 fund classifications',
            'ESG data collection procedures'
          ],
          ai_generated: true,
          ai_confidence: 92.1,
          status: 'predicted',
          created_at: new Date().toISOString(),
          recommendations: [
            'Review ESG data quality and coverage',
            'Update fund prospectuses if needed',
            'Coordinate with ESG data providers'
          ]
        }
      ];
      
      setEvents(prev => [...prev, ...aiEvents]);
      setShowAIDialog(false);
    } catch (error) {
      LoggerService.error('Error generating AI events:', error);
    } finally {
      setLoading(false);
    }
  };

  const createEvent = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const event: CalendarEvent = {
        id: `manual-${Date.now()}`,
        ...newEvent,
        event_date: format(newEvent.event_date, 'yyyy-MM-dd'),
        ai_generated: false,
        status: 'upcoming',
        created_at: new Date().toISOString()
      };
      
      setEvents(prev => [...prev, event]);
      setShowCreateDialog(false);
      setNewEvent({
        title: '',
        description: '',
        event_date: new Date(),
        event_type: 'deadline',
        importance: 'medium',
        regulatory_body: '',
        jurisdiction: 'EU',
        affected_entities: [],
        requirements: []
      });
    } catch (error) {
      LoggerService.error('Error creating event:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateEventStatus = async (eventId: string, newStatus: string) => {
    try {
      setEvents(prev => 
        prev.map(event => 
          event.id === eventId 
            ? { ...event, status: newStatus }
            : event
        )
      );
    } catch (error) {
      LoggerService.error('Error updating event status:', error);
    }
  };

  const filterEvents = (events: CalendarEvent[]) => {
    return events.filter(event => {
      if (filters.start_date && isBefore(new Date(event.event_date), filters.start_date)) {
        return false;
      }
      if (filters.end_date && isAfter(new Date(event.event_date), filters.end_date)) {
        return false;
      }
      if (filters.event_types && !filters.event_types.includes(event.event_type)) {
        return false;
      }
      if (filters.jurisdictions && !filters.jurisdictions.includes(event.jurisdiction)) {
        return false;
      }
      if (filters.importance_levels && !filters.importance_levels.includes(event.importance)) {
        return false;
      }
      if (filters.ai_generated_only !== undefined && event.ai_generated !== filters.ai_generated_only) {
        return false;
      }
      return true;
    });
  };

  const filteredEvents = filterEvents(events);
  const criticalEvents = filteredEvents.filter(e => e.importance === 'critical');

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Regulatory Calendar
          </Typography>
          <Typography variant="body1" color="text.secondary">
            AI-powered regulatory event tracking and deadline management
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<SmartToy />}
            onClick={() => setShowAIDialog(true)}
            sx={{ mr: 2 }}
          >
            Generate AI Events
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setShowCreateDialog(true)}
          >
            Add Event
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Event color="primary" />
                  <Box sx={{ ml: 2 }}>
                    <Typography color="text.secondary" gutterBottom>
                      Total Events
                    </Typography>
                    <Typography variant="h4">
                      {statistics.summary.total_events}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Warning color="error" />
                  <Box sx={{ ml: 2 }}>
                    <Typography color="text.secondary" gutterBottom>
                      Critical Events
                    </Typography>
                    <Typography variant="h4">
                      {statistics.summary.critical_events}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Schedule color="warning" />
                  <Box sx={{ ml: 2 }}>
                    <Typography color="text.secondary" gutterBottom>
                      Upcoming Deadlines
                    </Typography>
                    <Typography variant="h4">
                      {statistics.summary.upcoming_deadlines}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SmartToy color="info" />
                  <Box sx={{ ml: 2 }}>
                    <Typography color="text.secondary" gutterBottom>
                      AI Generated
                    </Typography>
                    <Typography variant="h4">
                      {statistics.summary.ai_generated_events}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(_e, newValue) => setSelectedTab(newValue)}>
          <Tab label="Calendar View" />
          <Tab label="Upcoming Deadlines" />
          <Tab label="Critical Events" />
          <Tab label="AI Insights" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Grid container spacing={3}>
          {/* Events List */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Regulatory Events
                </Typography>
                {loading ? (
                  <Typography>Loading events...</Typography>
                ) : (
                  <List>
                    {filteredEvents.map((event) => (
                      <ListItem 
                        key={event.id} 
                        divider
                        onClick={() => setSelectedEvent(event)}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      >
                        <ListItemIcon>
                          {eventTypeIcons[event.event_type as keyof typeof eventTypeIcons]}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1">
                                {event.title}
                              </Typography>
                              <Chip
                                label={event.importance}
                                color={importanceColors[event.importance as keyof typeof importanceColors]}
                                size="small"
                              />
                              <Chip
                                label={event.status}
                                color={statusColors[event.status as keyof typeof statusColors]}
                                size="small"
                              />
                              {event.ai_generated && (
                                <Tooltip title={`AI Confidence: ${event.ai_confidence}%`}>
                                  <SmartToy color="info" fontSize="small" />
                                </Tooltip>
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {event.description}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Due: {format(new Date(event.event_date), 'PPP')} | {event.regulatory_body}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Filters
                </Typography>
                {/* Add filter controls here */}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Notifications />}
                  sx={{ mb: 1 }}
                >
                  Set Alerts
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<TrendingUp />}
                >
                  View Analytics
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {selectedTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Upcoming Deadlines (Next 30 Days)
            </Typography>
            <List>
              {upcomingDeadlines.map((event) => (
                <ListItem key={event.id} divider>
                  <ListItemIcon>
                    <Schedule color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary={event.title}
                    secondary={`Due: ${format(new Date(event.event_date), 'PPP')} | ${event.regulatory_body}`}
                  />
                  <Chip
                    label={event.importance}
                    color={importanceColors[event.importance as keyof typeof importanceColors]}
                    size="small"
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {selectedTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Critical Priority Events
            </Typography>
            <Alert severity="error" sx={{ mb: 2 }}>
              These events require immediate attention due to their critical importance.
            </Alert>
            <List>
              {criticalEvents.map((event) => (
                <ListItem key={event.id} divider>
                  <ListItemIcon>
                    <Warning color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={event.title}
                    secondary={`Due: ${format(new Date(event.event_date), 'PPP')} | ${event.regulatory_body}`}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {selectedTab === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              AI-Generated Insights & Predictions
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              These events were predicted by AI based on regulatory patterns and recent changes.
            </Alert>
            <List>
              {events.filter(e => e.ai_generated).map((event) => (
                <ListItem key={event.id} divider>
                  <ListItemIcon>
                    <SmartToy color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>{event.title}</Typography>
                        <Chip
                          label={`${event.ai_confidence}% confidence`}
                          color="info"
                          size="small"
                        />
                      </Box>
                    }
                    secondary={`Predicted: ${format(new Date(event.event_date), 'PPP')} | ${event.regulatory_body}`}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Event Details Dialog */}
      <Dialog 
        open={selectedEvent !== null} 
        onClose={() => setSelectedEvent(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedEvent && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {eventTypeIcons[selectedEvent.event_type as keyof typeof eventTypeIcons]}
                <Typography variant="h6">{selectedEvent.title}</Typography>
                {selectedEvent.ai_generated && (
                  <Chip label={`AI: ${selectedEvent.ai_confidence}%`} color="info" size="small" />
                )}
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" paragraph>
                {selectedEvent.description}
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Due Date
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(selectedEvent.event_date), 'PPP')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Priority
                  </Typography>
                  <Chip
                    label={selectedEvent.importance}
                    color={importanceColors[selectedEvent.importance as keyof typeof importanceColors]}
                    size="small"
                  />
                </Grid>
              </Grid>

              {selectedEvent.requirements.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Requirements
                  </Typography>
                  <List dense>
                    {selectedEvent.requirements.map((req, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircle fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={req} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {selectedEvent.recommendations && selectedEvent.recommendations.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    AI Recommendations
                  </Typography>
                  <List dense>
                    {selectedEvent.recommendations.map((rec, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <SmartToy fontSize="small" color="info" />
                        </ListItemIcon>
                        <ListItemText primary={rec} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedEvent(null)}>
                Close
              </Button>
              <Button 
                variant="contained"
                onClick={() => updateEventStatus(selectedEvent.id, 'in_progress')}
              >
                Start Working
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create Event Dialog */}
      <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Regulatory Event</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Title"
                value={newEvent.title}
                onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={newEvent.description}
                onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePicker
                label="Event Date"
                value={newEvent.event_date}
                onChange={(date) => date && setNewEvent({...newEvent, event_date: date})}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={newEvent.event_type}
                  onChange={(e) => setNewEvent({...newEvent, event_type: e.target.value})}
                >
                  {eventTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newEvent.importance}
                  onChange={(e) => setNewEvent({...newEvent, importance: e.target.value})}
                >
                  {importanceLevels.map(level => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Regulatory Body"
                value={newEvent.regulatory_body}
                onChange={(e) => setNewEvent({...newEvent, regulatory_body: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={createEvent} disabled={loading}>
            {loading ? 'Creating...' : 'Create Event'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Generation Dialog */}
      <Dialog open={showAIDialog} onClose={() => setShowAIDialog(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToy color="info" />
            Generate AI Calendar Events
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use AI to predict upcoming regulatory events based on current regulatory landscape and patterns.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Jurisdiction</InputLabel>
                <Select
                  value={aiRequest.jurisdiction}
                  onChange={(e) => setAIRequest({...aiRequest, jurisdiction: e.target.value})}
                >
                  {jurisdictions.map(jurisdiction => (
                    <MenuItem key={jurisdiction} value={jurisdiction}>{jurisdiction}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Time Horizon (Days)"
                value={aiRequest.time_horizon_days}
                onChange={(e) => setAIRequest({...aiRequest, time_horizon_days: parseInt(e.target.value)})}
                inputProps={{ min: 1, max: 365 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAIDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            startIcon={<SmartToy />}
            onClick={generateAIEvents} 
            disabled={loading}
          >
            {loading ? 'Generating...' : 'Generate Events'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegulatoryCalendar;